import React, { useEffect, useState } from "react";
import { FaMapMarkerAlt } from "react-icons/fa";
import {
  AiOutlineMail,
  AiOutlinePhone,
  AiOutlineCalendar,
  AiOutlineHome,
} from "react-icons/ai";
import noimage from '../asset/noimage.png';
import profile from "../asset/noimage.png";
import SummaryApi from "../common";
import { useAuth } from "../helpers/AuthContext"; // Import AuthContext
import { useNavigate } from "react-router-dom"; // Optional: for redirect

const StudentProfile = () => {
  const [studentData, setStudentData] = useState(null);
  const { studentId, authLoaded } = useAuth(); // Use studentId from context
  const navigate = useNavigate();

  useEffect(() => {
    // Retrieve studentId from localStorage if not available in context
    const storedStudentId = localStorage.getItem('student_id');
    if (!studentId && storedStudentId) {
      console.log("Found student ID in localStorage.");
      setStudentData({ student_id: storedStudentId });
    }

    if (!authLoaded) return;

    if (!studentId && !storedStudentId) {
      console.warn("No student_id found. Redirecting to login...");
      navigate("/student-login");
      return;
    }

    // Fetch student data using student_id
    fetch(SummaryApi.getStudentById.url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ student_id: parseInt(studentId || storedStudentId) }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status) {
          console.log("Fetched student data:", data.data); // Log the whole response
          console.log("Student image URL:", data.data.student_image); // Log image URL
          setStudentData(data.data);
        } else {
          console.error("Failed to fetch student data");
        }
      })
      .catch((error) => console.error("API Error:", error));

  }, [studentId, authLoaded, navigate]);

  useEffect(() => {
    if (studentData && studentData.student_id) {
      // Store the student_id in localStorage when student data is fetched
      localStorage.setItem('student_id', studentData.student_id);
    }
  }, [studentData]);

  const handleEdit = () => {
    if (studentData && studentData.student_id) {
      // Store student ID in sessionStorage for the edit form
      sessionStorage.setItem("edit_student_id", studentData.student_id.toString());

      // Encode the ID (Base64) for URL
      const encodedId = btoa(studentData.student_id.toString());

      // Open the edit form page in a new tab/window
      window.open(`/student-panel/edit-student?id=${encodedId}`, "_blank");
    } else {
      alert("Student data not loaded yet.");
    }
  };

  if (!authLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 p-10 text-blue-900 dark:text-blue-400 font-semibold">Loading authentication...</div>
    );
  }

  if (!studentData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 p-10 text-blue-900 dark:text-blue-400 font-semibold">Loading profile...</div>
    );
  }

  // Destructure student data
  const {
    first_name,
    last_name,
    email,
    mobile_number,
    address,
    city,
    academic_course,
    student_image,
    college_name,
    created_date,
  } = studentData;

return (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6 xl:px-8">
    <div className="max-w-7xl mx-auto">
      {/* Header Section */}
      <header className="bg-[#1C3664] dark:bg-blue-900 text-white px-4 sm:px-6 py-4 sm:py-5 mb-6 sm:mb-8 lg:mb-10 rounded-xl shadow-lg">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-center sm:text-left">Student Profile</h1>
        <p className="text-sm text-blue-200 dark:text-blue-300 mt-1 text-center sm:text-left">
          View and manage your academic information.
        </p>
      </header>

      {/* Profile Section */}
      <div className="flex flex-col lg:flex-row items-center lg:items-start gap-6 sm:gap-8 lg:gap-10">
        {/* Profile Image */}
        <div className="w-full lg:w-auto flex justify-center">
          <img
            src={
              student_image
                ? `${SummaryApi.Images?.url?.replace("api", "")}${student_image}`
                : noimage
            }
            alt={`${first_name} ${last_name}`}
            onError={(e) => {
              console.error("Image failed to load:", e.target.src);
              e.target.src = noimage;
            }}
            className="w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 rounded-xl object-cover border-4 border-white dark:border-gray-700 shadow-xl"
          />
        </div>

        {/* Profile Info */}
        <div className="flex-1 w-full text-center lg:text-left">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-center lg:justify-start">
            <div className="flex items-center gap-2 text-[#1C3664] dark:text-blue-400 font-bold text-xl sm:text-2xl">
              {first_name} {last_name}
            </div>
          </div>
          
          <div className="flex items-center gap-2 justify-center lg:justify-start mt-2">
            <FaMapMarkerAlt className="text-gray-500 dark:text-gray-400" />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {city === "N/A" ? "Unknown" : city}
            </span>
          </div>
          
          <h2 className="text-[#1C3664] dark:text-blue-300 text-lg sm:text-xl font-semibold mt-3 cursor-pointer hover:underline text-center lg:text-left">
            {academic_course}
          </h2>
          
          <p className="text-gray-600 dark:text-gray-400 mt-2 mb-4 text-center lg:text-left">
            Manage your personal and academic details.
          </p>

          <div className="flex justify-center lg:justify-start">
            <button
              onClick={handleEdit}
              className="bg-[#1C3664] dark:bg-blue-700 hover:bg-[#16375c] dark:hover:bg-blue-800 text-white font-semibold px-5 py-3 rounded-md transition shadow-md w-full sm:w-auto"
            >
              Edit Profile
            </button>
          </div>
        </div>
      </div>

      {/* Personal Information Section */}
      <hr className="mt-6 sm:mt-8 lg:mt-[5%] w-full max-w-[90%] mx-auto border-0 bg-gray-300 dark:bg-gray-700 h-px" />
      
      <div className="max-w-7xl mx-auto mt-6 sm:mt-8 p-4 sm:p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
        <div className="bg-[#1C3664] dark:bg-blue-900 text-white font-semibold text-base sm:text-lg px-4 sm:px-6 py-3 rounded-t-xl">
          Personal Information
        </div>
        
        <form className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6 text-gray-700 dark:text-gray-300">
          {/* Left Column */}
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block font-semibold text-[#1C3664] dark:text-blue-400 mb-1 text-sm sm:text-base">
                First Name
              </label>
              <input 
                type="text" 
                readOnly 
                defaultValue={first_name} 
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-[#1C3664]/20 dark:focus:ring-blue-500/20 focus:border-[#1C3664] dark:focus:border-blue-500 text-sm sm:text-base" 
              />
            </div>
            
            <div>
              <label className="block font-semibold text-[#1C3664] dark:text-blue-400 mb-1 text-sm sm:text-base">
                Email
              </label>
              <input 
                type="email" 
                readOnly 
                defaultValue={email} 
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed text-sm sm:text-base" 
              />
            </div>
            
            <div>
              <label className="block font-semibold text-[#1C3664] dark:text-blue-400 mb-1 text-sm sm:text-base">
                Address
              </label>
              <textarea 
                rows="3" 
                readOnly 
                defaultValue={address === "N/A" ? "" : address} 
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 resize-none cursor-not-allowed text-sm sm:text-base" 
              />
            </div>
            
            <div>
              <label className="block font-semibold text-[#1C3664] dark:text-blue-400 mb-1 text-sm sm:text-base">
                Date of Admission
              </label>
              <input
                type="text"
                readOnly
                defaultValue={
                  created_date
                    ? `${String(new Date(created_date).getDate()).padStart(2, "0")}-${String(
                        new Date(created_date).getMonth() + 1
                      ).padStart(2, "0")}-${new Date(created_date).getFullYear()}`
                    : ""
                }
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed text-sm sm:text-base"
              />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block font-semibold text-[#1C3664] dark:text-blue-400 mb-1 text-sm sm:text-base">
                Last Name
              </label>
              <input 
                type="text" 
                readOnly 
                defaultValue={last_name} 
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-[#1C3664]/20 dark:focus:ring-blue-500/20 focus:border-[#1C3664] dark:focus:border-blue-500 text-sm sm:text-base" 
              />
            </div>
            
            <div>
              <label className="block font-semibold text-[#1C3664] dark:text-blue-400 mb-1 text-sm sm:text-base">
                Phone
              </label>
              <input 
                readOnly
                type="text"
                defaultValue={mobile_number}
                maxLength={10}
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^\d{0,10}$/.test(value)) {
                    e.target.value = value;
                  } else {
                    e.target.value = value.slice(0, 10);
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value.length !== 10) {
                    alert("Please enter a valid 10-digit mobile number.");
                    e.target.focus();
                  }
                }}
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-[#1C3664]/20 dark:focus:ring-blue-500/20 focus:border-[#1C3664] dark:focus:border-blue-500 text-sm sm:text-base"
              />
            </div>

            <div>
              <label className="block font-semibold text-[#1C3664] dark:text-blue-400 mb-1 text-sm sm:text-base">
                City
              </label>
              <input 
                readOnly 
                type="text" 
                defaultValue={city === "N/A" ? "" : city} 
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-[#1C3664]/20 dark:focus:ring-blue-500/20 focus:border-[#1C3664] dark:focus:border-blue-500 text-sm sm:text-base" 
              />
            </div>
          </div>
        </form>
      </div>

      {/* Education Details */}
      <div className="max-w-7xl mx-auto mt-6 sm:mt-8 p-4 sm:p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
        <div className="bg-[#1C3664] dark:bg-blue-900 text-white font-semibold text-base sm:text-lg px-4 sm:px-6 py-3 rounded-t-xl">
          Education Details
        </div>
        
        <form className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6 text-gray-700 dark:text-gray-300">
          {/* Left Column */}
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block font-semibold text-[#1C3664] dark:text-blue-400 mb-1 text-sm sm:text-base">
                University/College Name
              </label>
              <input 
                type="text" 
                readOnly
                defaultValue={studentData.college_id > 0 ? studentData.college_name : studentData.clg_name} 
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-[#1C3664]/20 dark:focus:ring-blue-500/20 focus:border-[#1C3664] dark:focus:border-blue-500 text-sm sm:text-base" 
              />
            </div>
            
            <div>
              <label className="block font-semibold text-[#1C3664] dark:text-blue-400 mb-1 text-sm sm:text-base">
                Academic Year
              </label>
              <div className="flex space-x-2">
                <input 
                  type="text" 
                  readOnly 
                  defaultValue={studentData.academic_year} 
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed text-sm sm:text-base" 
                />
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4 sm:space-y-6">
            <div>
              <label className="block font-semibold text-[#1C3664] dark:text-blue-400 mb-1 text-sm sm:text-base">
                Academic Qualification
              </label>
              <input 
                readOnly 
                type="text" 
                defaultValue={academic_course} 
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 sm:py-3 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-[#1C3664]/20 dark:focus:ring-blue-500/20 focus:border-[#1C3664] dark:focus:border-blue-500 text-sm sm:text-base" 
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
);
};

export default StudentProfile;